/**
 * cart-promo-effects.js
 *
 * Shopify Cart Ajax API 호출 effect 모듈 (Phase 3, 2026-05-27).
 *
 * 책임: /cart/add.js, /cart/change.js, /cart/update.js, /discount/CODE 호출 + cart line 단위 mutation.
 * this.state 미참조. window.CartPromoEffects namespace.
 *
 * 의존:
 *   - window.CartCouponHubUtils (shopifyCartVariantId, promotionProperties)
 *
 * 로드 순서 (sections/main-cart.liquid + snippets/cart-drawer.liquid):
 *   1. cart-coupon-utils.js
 *   2. cart-promo-effects.js  ← 본 파일
 *   3. cart-coupon-hub.js
 *
 * 회귀 박제:
 *   - cart-change.js qty reset (2026-05-24) — changeLineProperties 가 quantity 명시 전달
 *   - cart/update.js qty=0 vs cart/change.js 422 (2026-05-26) — removeCartLine 은 update.js
 *   - addedByBxgy flag (2026-05-26) — clearOrRemoveLinePromotion 의 분기
 *   - _promotion_added_index salt (2026-05-27) — addPromotedCartItems 의 line key 분리
 *   - quantityOverride (2026-05-27) — anchor split qty=1 강제
 */
(function (global) {
  'use strict';

  const Utils = global.CartCouponHubUtils;
  if (!Utils) {
    console.warn('[cart-promo-effects] CartCouponHubUtils 미로드 — 로드 순서 확인');
  }

  const Effects = {

    // Shopify cart 에 discount code 적용/제거.
    //   code='' → /cart/update.js {discount:''} 로 모든 discount code 제거 (community pattern)
    //   code='FOO' → /discount/FOO?redirect=/cart.js 로 적용 (Shopify 표준, customerSelection 자동 검증)
    async applyDiscountCode(code) {
      if (!code) {
        try {
          await fetch(MISTO.localeUrl('/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ discount: '' }),
          });
        } catch (err) {
          console.warn('[cart-promo-effects] clear discount', err);
        }
        return;
      }
      const url = `/discount/${encodeURIComponent(code)}?redirect=/cart.js`;
      try {
        await fetch(url, { method: 'GET', credentials: 'same-origin' });
      } catch (err) {
        console.warn('[cart-promo-effects] apply discount', err);
      }
    },

    // 🔴 [중요] (2026-05-28) salt 제거 + qty 묶음. 이전 정책 (qty=1 별도 라인) 폐기.
    //    사유: 사용자 UX (라인 수 폭증). 회귀 회피는 group_id 가 담당.
    //    회귀 case: wiki/docs/bxgy-gy-line-qty-1-per-line-2026-05-27.md (폐기 marker)
    //    신 정책: docs/superpowers/plans/2026-05-28-cart-bxgy-y-only-modal.md
    //
    // 동작: 같은 variant + properties (group_id 포함) 라인은 Shopify auto-merge 로 한 라인 qty=N.
    //   다른 anchor 의 같은 variant gy 는 group_id 가 다르므로 properties 다름 → auto-merge 차단.
    async addPromotedCartItems(items, promo, bxgyGroupId = '') {
      const cartItems = (items || [])
        .flatMap((item) => {
          const id = Utils.shopifyCartVariantId(item.variantId || item.variantGid);
          if (!id) return [];
          const totalQty = Math.max(1, parseInt(item.quantity || '1', 10) || 1);
          const groupId = item.bxgyGroupId || bxgyGroupId || '';
          const properties = Utils.promotionProperties(promo, item.bxgyRole, false, true, groupId);
          return [{ id, quantity: totalQty, properties }];
        });
      if (!cartItems.length) throw new Error('cart/add missing variant ids');
      const res = await fetch(MISTO.localeUrl('/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: cartItems }),
      });
      if (!res.ok) {
        let message = `cart/add ${res.status}`;
        try {
          const payload = await res.json();
          message = payload.description || payload.message || message;
        } catch (_err) {
          // ignore non-JSON Shopify errors
        }
        throw new Error(message);
      }
    },

    // 🔴 properties 변경 시 Shopify line key 가 바뀜 (key = variant_id + properties hash).
    // 한 confirm 흐름에서 동일 cart 라인의 setLinePromotion 이 여러 번 호출되면 stale lineKey 로 'cart line not found' throw.
    // variantId 가 있으면 fallback 매칭 + 최신 key 사용.
    // 🔴 quantity 명시 전달 (2026-05-24) — Shopify cart/change.js 가 properties-only body 일 때
    //    qty 가 1 로 reset 되는 회귀. item.quantity 그대로 전달해 보존.
    //    회귀 case: docs/policy/cart-coupon-hub.md §회귀 함정 (promo 적용 qty=1 강제)
    // 🔴 quantityOverride (2026-05-27) — BXGY anchor split 시 원본 라인을 qty=1 로 강제 축소.
    //    회귀 case: wiki/docs/bxgy-anchor-split-qty-1-per-line-2026-05-27.md
    async changeLineProperties(lineKey, buildProperties, variantId, quantityOverride) {
      if (!lineKey && !variantId) return;
      const cartRes = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
      if (!cartRes.ok) throw new Error(`cart ${cartRes.status}`);
      const cart = await cartRes.json();
      const items = cart.items || [];
      let item = lineKey ? items.find((line) => line.key === lineKey) : null;
      // 🔴 [독립성] (2026-05-28) lineKey stale (properties 변경으로 re-key) 시 variant fallback.
      //   단 같은 variant 라인이 2개 이상이면 어느 게 대상인지 모호 → 추측 매칭 금지.
      //   variant 단독으로 첫 라인 잡으면 다른 anchor/group/standalone 을 오타겟 (cross-line 오염).
      //   유일 매칭일 때만 fallback. 모호하면 item=null → throw (fail loud, 잘못된 라인 mutate 안 함).
      //   회귀 case: wiki/docs/bxgy-multi-anchor-same-promo-cross-clear-2026-05-28.md
      if (!item && variantId) {
        const vid = String(variantId);
        const matches = items.filter((line) => String(line.variant_id) === vid);
        if (matches.length === 1) item = matches[0];
      }
      if (!item) throw new Error(`cart line not found: ${lineKey || variantId}`);
      const properties = buildProperties(item.properties || {});
      const nextQuantity = (typeof quantityOverride === 'number' && quantityOverride > 0)
        ? quantityOverride
        : item.quantity;
      const res = await fetch(MISTO.localeUrl('/cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: item.key, properties, quantity: nextQuantity }),
      });
      if (!res.ok) throw new Error(`cart/change ${res.status}`);
    },

    // 🔴 (2026-05-28) BXGY group cleanup — 같은 group_id 의 자식 라인 모두 cart 에서 제거 + (선택) anchor properties wipe.
    //   재사용처: confirmBxgy (modal 재진입 reset, C11), dropdown 해제 (C14), anchor 삭제 cascade (C15).
    //   matching 규칙: _promotion_group_id 우선, 없으면 _promotion_id legacy fallback.
    //   anchor 식별: _promotion_bxgy_anchor === 'true'.
    async cleanupBxgyGroup(groupKey, options = {}) {
      if (!groupKey) return;
      const cartRes = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();
      const items = cart.items || [];
      const matchesGroup = (i) => {
        const g = i.properties?._promotion_group_id || '';
        const p = i.properties?._promotion_id || '';
        return g === groupKey || (!g && p === groupKey);
      };
      // 1. 자식 라인 제거
      const childKeys = items
        .filter(matchesGroup)
        .filter((i) => i.properties?._promotion_bxgy_anchor !== 'true')
        .map((i) => i.key);
      if (childKeys.length) {
        const updates = {};
        childKeys.forEach((k) => { updates[k] = 0; });
        await fetch(MISTO.localeUrl('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates }),
        });
      }
      // 2. anchor 라인 properties wipe (옵션)
      if (options.includeAnchor !== false) {
        const anchorItems = items
          .filter(matchesGroup)
          .filter((i) => i.properties?._promotion_bxgy_anchor === 'true');
        for (const anchorItem of anchorItems) {
          const anchorCell = document.querySelector(`[data-cart-promo-cell][data-line-key="${anchorItem.key}"]`);
          if (anchorCell) {
            await Effects.clearLinePromotion(anchorCell);
          }
        }
      }
    },

    // 🔴 /cart/update.js updates qty=0 — SOLD-OUT 함정 회피 (cart-stock-guard 와 동일 패턴).
    //   /cart/change.js 는 솔드아웃 라인에서 422. updates 는 status 200 으로 라인 제거.
    //   회귀 case: wiki/docs/cart-stock-guard-update-vs-change-2026-05-26.md
    async removeCartLine(cell) {
      if (!cell) return;
      const lineKey = cell.dataset.lineKey;
      if (!lineKey) return;
      try {
        await fetch(MISTO.localeUrl('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: { [lineKey]: 0 } }),
        });
      } catch (err) {
        console.warn('[cart-promo-effects] removeCartLine', err);
      }
    },

    // 라인의 promo properties 모두 clear transformer (SSOT).
    // Shopify cart change.js 가 hidden `_` prefix properties 빈 객체 변경 불가 → __none__ sentinel 패턴.
    // cart-promo-cell.liquid (L21-23) 가 _promotion_code === '__none__' 보면 빈 문자열로 처리 → dropdown "No promotion".
    promoClearTransformer(properties) {
      const next = { ...(properties || {}), _promotion_code: '__none__' };
      delete next.promotion_code;
      delete next.promotion_id;
      delete next.promotion_title;
      delete next.promotion_type;
      delete next.promotion_bxgy_role;
      delete next.promotion_bxgy_anchor;
      delete next._promotion_id;
      delete next._promotion_title;
      delete next._promotion_type;
      delete next._promotion_bxgy_role;
      delete next._promotion_bxgy_anchor;
      delete next._promotion_added_by_bxgy;
      // 🔴 (2026-05-28) group_id 도 wipe — 안 지우면 SSR cart-promo-cell 의 data-applied-promotion-group-id 잔존 → 재진입 시 stale group_id 참조.
      delete next._promotion_group_id;
      return next;
    },

    // 라인의 promo properties 모두 clear (라인은 유지, 정가 회복). DOM cell 의존.
    async clearLinePromotion(cell) {
      if (!cell) return;
      await Effects.clearLineByKey(cell.dataset.lineKey, cell.dataset.variantId);
    },

    // 🔴 (2026-05-28 #25 fix) DOM cell 없이 lineKey + variantId 로 promo clear (cart-promo-cleanup 등 외부 호출용).
    //   guest-cart-promo-guard / employee-payment-popup 의 cleanup 흐름에서 cell 없는 페이지 대응.
    async clearLineByKey(lineKey, variantId) {
      if (!lineKey) return;
      await Effects.changeLineProperties(lineKey, Effects.promoClearTransformer, variantId);
    },

    // 🔴 분기 (2026-05-26):
    //   BXGY 카탈로그에서 add 된 라인(_promotion_added_by_bxgy='true') → cart 라인 자체 제거.
    //   사용자가 원래 카트에 담은 라인 → properties 만 clear (라인 보존, 정가 회복).
    //
    // 🔴 [중요] removeCartLine 은 반드시 "cell.dataset.lineKey 의 *실제* cart 라인이 catalog-added BXGY" 일 때만.
    //   회귀 case: wiki/docs/bxgy-reselect-removes-standalone-same-variant-2026-05-28.md
    //   dataset(appliedPromotionAddedByBxgy) 만 믿거나 variant 로 fallback 매칭하면, 같은 variant 의
    //   plain standalone 라인을 오삭제함 (anchor·gift·standalone 셋 다 같은 variant 일 때 dataset.lineKey 가 꼬임).
    //   판정 SSOT = cart.js 의 *정확한 lineKey* 라인 properties. variant 추측 삭제 절대 금지.
    //   라인이 catalog-added 아니거나(=사용자 원본) lineKey stale(이미 제거) 면 → 라인 보존.
    async clearOrRemoveLinePromotion(cell) {
      if (!cell) return;
      const lineKey = cell.dataset.lineKey;
      try {
        const cart = await (await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' })).json();
        const items = cart.items || [];
        const item = lineKey ? items.find((it) => it.key === lineKey) : null;
        if (item && item.properties && item.properties._promotion_added_by_bxgy === 'true') {
          await Effects.removeCartLine(cell);
          return;
        }
        // lineKey stale (이미 제거됨 등) → 추측 삭제 금지, no-op
        if (!item) return;
      } catch (err) {
        console.warn('[cart-promo-effects] clearOrRemoveLinePromotion verify', err);
      }
      // 사용자 원본 라인 (catalog-added 아님) → properties 만 clear, 라인 보존
      await Effects.clearLinePromotion(cell);
    },
  };

  global.CartPromoEffects = Effects;
})(typeof window !== 'undefined' ? window : globalThis);
